import { memo, useEffect, useRef } from 'react';
import cx from 'classnames';
import { createGlbScene, type GlbSceneApi } from '../../scene/glb/createGlbScene';
import {
  claimPoiArtifact,
  releasePoiArtifact,
} from '../../scene/glb/poiArtifactHost';
import type { GlbSceneConfig } from '../../scene/glb/glbSceneConfig';
import type { Sight } from '../../types';
import { sceneLoadingFinished } from '../../state/sceneLoading';
import styles from './GlbSceneCanvas.module.css';

interface GlbSceneCanvasProps {
  config: GlbSceneConfig;
  sights: Sight[];
  onApiReady?: (api: GlbSceneApi | null) => void;
  /** Expand canvas to full viewport (no chat panel offset). */
  fullscreen?: boolean;
  /** Do not fire global sceneLoading* events. */
  suppressLoadingEvents?: boolean;
  /**
   * PoiArtifact claimant mode: когда задан, canvas берётся из `poiArtifactHost`
   * (единый рендерер на артефакты). Значение — уникальный `claimantId`
   * (`'fullscreen'` или `cardId`). Canvas физически переезжает между
   * container'ами без пересоздания WebGL-контекста.
   */
  poiArtifactClaimant?: string;
  /** Включить zoom у OrbitControls (только для artifact claim'ов). */
  enableZoom?: boolean;
}

const GlbSceneCanvasComponent = ({
  config,
  sights,
  onApiReady,
  fullscreen,
  suppressLoadingEvents,
  poiArtifactClaimant,
  enableZoom,
}: GlbSceneCanvasProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sightsRef = useRef(sights);
  sightsRef.current = sights;

  useEffect(() => {
    if (!containerRef.current) return;

    const hasPreview = config.preview != null;

    if (poiArtifactClaimant) {
      claimPoiArtifact({
        claimantId: poiArtifactClaimant,
        container: containerRef.current,
        config,
        enableZoom,
        onReady: (api) => onApiReady?.(api),
      });
      return () => {
        onApiReady?.(null);
        releasePoiArtifact(poiArtifactClaimant);
      };
    }

    const api = createGlbScene({
      container: containerRef.current,
      config,
      sights: sightsRef.current,
      suppressLoadingEvents,
      ignoreViewOffset: fullscreen,
      onReady: () => {
        onApiReady?.(api);
        if (!hasPreview && !suppressLoadingEvents) {
          sceneLoadingFinished();
        }
      },
    });

    return () => {
      onApiReady?.(null);
      api.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, poiArtifactClaimant]);

  return (
    <div
      ref={containerRef}
      className={cx(styles.canvasRoot, fullscreen && styles.canvasRootFullscreen)}
    />
  );
};

export const GlbSceneCanvas = memo(GlbSceneCanvasComponent);
GlbSceneCanvas.displayName = 'GlbSceneCanvas';
